"use client";

import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { Sheet } from "@/components/shell/Sheet";
import { Button } from "@/components/ui/Button";
import { MoneyText } from "@/components/ui/MoneyText";
import { Mascot } from "@/components/brand/Mascot";
import { MoneyTalkCard } from "./MoneyTalkCard";
import { goalDisplay } from "@/lib/utils/goalDisplay";
import type {
  RecapFamilyGoalProgress,
  RecapPerChild,
  RecapPersonalGoal,
  WeeklyRecap,
} from "@/lib/hooks/useWeeklyRecap";

type Props = {
  open: boolean;
  onClose: () => void;
  recap: WeeklyRecap;
};

export function FridayRecapSheet({ open, onClose, recap }: Props) {
  const dateLabel = format(new Date(), "EEEE · d MMMM", { locale: pl });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      footer={
        <Button variant="secondary" block onClick={onClose}>
          Zwiń podsumowanie
        </Button>
      }
    >
      <div className="flex flex-col gap-3.5">
        {/* Hero */}
        <div
          className="relative overflow-hidden rounded-[22px] border border-hay-bd bg-hay px-4 pb-4 pt-4.5 text-center flex flex-col items-center gap-1.5"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 30%, rgba(255,201,60,0.4), transparent 55%)",
          }}
        >
          <div
            className="text-[11px] font-bold uppercase tracking-[0.04em]"
            style={{ color: "#7A5A00" }}
          >
            {dateLabel}
          </div>
          <div className="font-display text-[22px] font-semibold leading-[1.15] text-navy" style={{ letterSpacing: "-0.025em" }}>
            {recap.title}
          </div>
          <Mascot pose="peace" size={92} shadow className="mt-1" />
        </div>

        {/* Stats 2x2 — kolejność wg PRODUCT.md.
            Pomoc rodzinie / offline / zrobione = całe gospodarstwo
            (z breakdown'em per dziecko w sub).
            Zadania dodatkowe = TYLKO viewer (jego zarobki). */}
        <div className="grid grid-cols-2 gap-2">
          <StatCard
            label="Pomoc rodzinie"
            value={
              <span className="font-display text-[22px] font-semibold text-help" style={{ letterSpacing: "-0.025em" }}>
                {recap.familyHelpCount} <span className="text-[16px] opacity-80">💛</span>
              </span>
            }
            sub={breakdownLabel(recap.familyHelpPerChild)}
          />
          <StatCard
            label="Aktywność poza ekranem"
            value={
              <span className="font-display text-[22px] font-semibold text-green" style={{ letterSpacing: "-0.025em" }}>
                {recap.offlineCount} <span className="text-[16px] opacity-80">🌳</span>
              </span>
            }
            sub={breakdownLabel(recap.offlinePerChild)}
          />
          <StatCard
            label="Twoje zadania dodatkowe"
            value={<MoneyText minor={recap.personalEarnedMinor} tone="money" className="text-[22px] font-display font-semibold" />}
            sub={diffLabel(recap.diffPersonalEarnedMinor)}
          />
          <StatCard
            label="Zadań zrobionych"
            value={
              <span className="font-display text-[22px] font-semibold text-navy" style={{ letterSpacing: "-0.025em" }}>
                {recap.approvedCount}
              </span>
            }
            sub={breakdownLabel(recap.approvedPerChild)}
          />
        </div>

        {/* Top moment */}
        {recap.topMoment ? (
          <div className="flex items-center gap-3 rounded-[16px] border border-line bg-card px-4 py-3.5">
            <div className="text-[28px] leading-none">🌟</div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
                Top moment
              </div>
              <div className="text-[14px] font-semibold text-navy mt-0.5 truncate">
                {recap.topMoment.choreTitle}
              </div>
              <div className="text-[11px] text-muted mt-0.5">
                {recap.topMoment.childName} · {recap.topMoment.dayName}
                {recap.topMoment.kind === "extra" && recap.topMoment.rewardMinor > 0 ? (
                  <> · {(recap.topMoment.rewardMinor / 100).toFixed(2).replace(".", ",")} zł</>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/* Postęp celów oszczędzania viewer'a + cel rodzinny */}
        {(recap.personalGoals.length > 0 || recap.familyGoal) ? (
          <div className="rounded-[16px] border border-line bg-card p-3.5">
            <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted mb-2.5">
              Postęp celów
            </div>
            <div className="flex flex-col gap-2">
              {recap.personalGoals.map((g) => (
                <PersonalGoalRow key={g.goalId} goal={g} />
              ))}
              {recap.familyGoal ? (
                <FamilyGoalRow progress={recap.familyGoal} />
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Nudge */}
        <div className="text-center text-[12px] italic text-muted py-1.5">
          Pogadajcie o tym przy obiedzie 💬
        </div>

        {/* Money Talk — pojawia się raz w tygodniu po recap'ie. Po "OK,
            pogadamy" (zapis rodzinny w moneyTalks/{weekKey}) zostaje cicha
            linijka z roczną liczbą rozmów. */}
        <MoneyTalkCard weekStart={recap.weekStart} />
      </div>
    </Sheet>
  );
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
}) {
  return (
    <div className="rounded-[16px] border border-line bg-card p-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
        {label}
      </div>
      <div className="mt-0.5 leading-tight">{value}</div>
      <div className="text-[11px] text-muted mt-0.5 truncate">{sub}</div>
    </div>
  );
}

function PersonalGoalRow({ goal }: { goal: RecapPersonalGoal }) {
  const balanceZl = Math.floor(goal.balanceMinor / 100);
  const targetZl = Math.floor(goal.targetMinor / 100);
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-navy">
        <span aria-hidden>{goal.emoji ?? "🎯"}</span>
        <span className="truncate flex-1">{goal.title}</span>
        <span className="font-num text-[11px] text-slate shrink-0">
          {balanceZl}/{targetZl} zł
        </span>
      </div>
      <div className="h-2 rounded-full bg-purple/15 overflow-hidden mt-1">
        <div
          className="h-full bg-purple rounded-full transition-[width] duration-300"
          style={{ width: `${goal.pct}%` }}
        />
      </div>
    </div>
  );
}

function FamilyGoalRow({ progress }: { progress: RecapFamilyGoalProgress }) {
  const meta = goalDisplay(progress.goal);
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-navy">
        <span aria-hidden>{progress.goal.emoji ?? meta.emoji}</span>
        <span className="truncate flex-1">{progress.goal.title}</span>
        <span className="font-num text-[11px] text-slate shrink-0">
          {progress.progress}/{progress.goal.targetCount}
        </span>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden mt-1"
        style={{ backgroundColor: `${meta.gradientFrom}26` }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{
            width: `${progress.pct}%`,
            backgroundColor: meta.gradientFrom,
          }}
        />
      </div>
    </div>
  );
}

function breakdownLabel(perChild: readonly RecapPerChild[]): string {
  if (perChild.length === 0) return "—";
  return perChild
    .slice(0, 3)
    .map((c) => `${c.name} ${c.count}`)
    .join(" · ");
}

function diffLabel(diffMinor: number): string {
  if (diffMinor === 0) return "tyle samo co tydzień temu";
  const sign = diffMinor > 0 ? "+" : "−";
  const abs = Math.abs(diffMinor);
  const zl = (abs / 100).toFixed(2).replace(".", ",").replace(/,00$/, "");
  return `${sign}${zl} zł vs zeszły`;
}
